<template>
  <VStockGridItemLayout
    :instances="productOptionsFiltered"
    :has-any-promo="grid.hasAnyPromo"
    id-key="uniqueSubscriptionId"
    promo-key="promotionEndDate"
    :initial-details-expanded="initialDetailsExpanded"
    :name="texts.productName"
  >
    <template #name>
      <div v-if="images.productLogoMyProductsResourcePath">
        <!-- #107 -->
        <img
          class="product-image"
          :src="'delegates/product/content/' + images.productLogoMyProductsResourcePath"
          :alt="images.productLogoMyProductsAlt"
          style="max-height: 100px"
          aria-hidden="true"
        />
      </div>

      <!-- #108 -->
      <span v-html="texts.productName" />
    </template>

    <template #sub-name>
      <!-- #D79 -->
      <span v-if="texts.productType" v-html="texts.productType" />
      <span v-else>{{ $t('substore.subscription_instance_headline_default') }}</span>
    </template>

    <template #small="{ inPromotionPeriod, optinPossible }">
      <template v-if="inPromotionPeriod">
        <div>
          <small v-html="ongoingPromotionInfo" />
        </div>
        <div v-if="optinPossible">
          <small v-if="texts.optinPossibleShortInfo">
            <span v-html="texts.optinPossibleShortInfo"></span>
          </small>
          <small v-else>
            {{ $t('substore.optin_possible_short_info') }}
          </small>
        </div>
      </template>
    </template>

    <template #minimumCommitmentPeriod="{ minimumCommitmentPeriodFactor, intervalUnit }">
      <template v-if="showMinimumCommitmentPeriod">
        <!-- HINT: subscriptionBookedByExtern = !DescElemToBeSetToFinalize -->
        <dt><!-- #S106 -->{{ $t('common.minimum_contract_period_label') }}</dt>
        <dd>
          <!-- #D48 -->
          <span v-if="texts.minimumCommitmentPeriodOverrideTextMyProducts" v-html="texts.minimumCommitmentPeriodOverrideTextMyProducts"></span>
          <span v-else>
            <span v-if="!minimumCommitmentPeriodFactor">
              <!--#S140 -->
              {{ $t('common.no_minimum_contract_period') }}
            </span>
            <span v-else class="text-nowrap">
              <!-- HINT: If minimumCommitmentPeriodOverrideTextMyProducts is 4, it gets multiplied by intervalLength: This is implemented at server side -->
              {{ minimumCommitmentPeriodFactor }}
              <!-- HINT: pluralization is done at server side -->
              {{ $t('common.' + intervalUnit) }}
            </span>
            <!-- #D50 -->
            <span v-if="!texts.minimumCommitmentPeriodOverrideTextMyProducts">
              <br />
              <span v-html="texts.minimumCommitmentPeriodTextMyProducts"></span>
            </span>
          </span>
        </dd>
      </template>
    </template>

    <template #bookingPeriod="instance">
      <div class="text-left">
        {{ instance.subscriptionStartDate }}
        -
        <span class="text-nowrap">{{ instance.subscriptionEndDate ? instance.subscriptionEndDate : $t('substore.unlimited') }}</span>
        <VButton
          :id="subscriptionTemplateId"
          :action.sync="grid.confirmation"
          :options="['optin']"
          :texts="texts"
          :subscription-template-id="subscriptionTemplateId"
          :product-option="instance"
          class="offset-top-1"
        />
      </div>
    </template>

    <template #promotionPeriod="{ subscriptionStartDate, promotionEndDate }">
      {{ subscriptionStartDate }}
      -
      <span class="text-nowrap">{{ promotionEndDate }}</span>
    </template>

    <template #state="{ status }">
      <span class="col-xl-sr-only col-l-sr-only col-m-sr-only col-s-sr-only">
        {{ $t(`substore.status_${status}`) }}
      </span>
      <VSvgIcon
        :name="statusSymbol(status, 'clock')"
        variant="outlined"
        size="large"
        class="h3 col-xs-hide icon--stroke"
        :style="{ color: statusColor(status) }"
        :title="$t(`substore.status_${status}`)"
      />
    </template>

    <template #buttons="instance">
      <VButton
        :id="subscriptionTemplateId"
        :action.sync="grid.confirmation"
        :options="['resetPromotionAndTrialStatus']"
        :texts="texts"
        :subscription-template-id="subscriptionTemplateId"
        :product-option="instance"
      />
    </template>

    <template #important="instance">
      <div v-if="instance.status !== 'inactive'" class="row second">
        <div class="col-l-12">
          <VInformationFields :fields="instance.informationFields" important />
        </div>
      </div>
    </template>

    <template #confirm>
      <VButtonConfirmation :id="subscriptionTemplateId" :action.sync="grid.confirmation" :texts="texts" />
    </template>

    <template #details="instance">
      <VDetails
        v-bind="{ subscriptionTemplateId, productOption: instance, texts, canNotSucceed, isSingleOption, orderCancellationAbortButtonLabelImportant }"
      />
    </template>
  </VStockGridItemLayout>
</template>

<script>
import VStockGridItemLayout from '@/components/Substore/StockGrid/ItemLayout';
import VInformationFields from './InformationFields';
import VSvgIcon from '@/components/UI/VSvgIcon.vue';
import { statusSymbol } from '@/utils/statusSymbol';

export default {
  name: 'Stockproduct',

  components: {
    VSvgIcon,
    VStockGridItemLayout,
    VInformationFields,
    VDetails: () => import('@/components/Substore/StockProduct/Details'),
    VButton: () => import('@/components/Substore/StockProduct/Button'),
    VButtonConfirmation: () => import('@/components/Substore/StockProduct/ButtonConfirmation'),
  },

  provide() {
    return {
      actionResult: ({
        name,
        content,
        supplier = () => content ?? this.$t(`substore.succeeded_${name}`),
        heading = 'substore.succeeded',
        type = 'SUCCESS',
        reload = false,
      }) => {
        this.$emit('message', { supplier, heading, type, reload });
      },
    };
  },

  inject: {
    grid: {
      from: 'grid',
      default: {},
    },
  },

  inheritAttrs: false,

  props: {
    texts: {
      type: Object,
      required: true,
    },

    images: {
      type: Object,
      required: true,
    },

    productOptions: {
      type: Array,
      required: true,
    },

    cntTerminated: {
      type: Number,
      required: true,
    },

    cntNotTerminated: {
      type: Number,
      required: true,
    },

    canNotSucceed: {
      type: Boolean,
      required: true,
    },

    optionType: {
      type: String,
      required: true,
    },

    subscriptionTemplateId: {
      type: String,
      required: true,
    },

    orderCancellationAbortButtonLabelImportant: {
      type: Boolean,
      required: false,
    },

    subscriptionType: {
      type: Number,
      required: true,
    },
  },

  computed: {
    productOptionsFiltered() {
      return this.productOptions.filter((po) => (this.optionType === 'booked' && !po.isTerminated) || (this.optionType === 'terminated' && po.isTerminated));
    },

    isSingleOption() {
      return (this.optionType === 'booked' && this.cntNotTerminated === 1) || (this.optionType === 'terminated' && this.cntTerminated === 1);
    },

    initialDetailsExpanded() {
      const result = {};
      this.productOptions
        .filter((o) => o.initialExpand)
        .forEach((option) => {
          option.initialExpand = null;
          result[option.uniqueSubscriptionId] = true;
        });
      return result;
    },

    showMinimumCommitmentPeriod() {
      return this.subscriptionType !== 2 && !this.subscriptionBookedByExtern && this.texts.minimumCommitmentPeriodOverrideTextMyProducts !== '-';
    },

    ongoingPromotionInfo() {
      if (this.texts.ongoingPromotionInfo === '-') {
        return '';
      }
      return this.texts.ongoingPromotionInfo ?? this.$t('substore.stock_overview_ongoing_promotion_info');
    },
  },

  methods: {
    statusSymbol,
    statusColor(status) {
      switch (status) {
        case 'reserved':
          return '#767676';
        case 'locked':
          return '#007faf';
        case 'active':
          return '#548d1c';
        case 'inactive':
          return '#767676';
        case 'optin':
          return '#548d1c';
        case 'terminated':
          return '#a10000';
        case 'failed':
          return '#a10000';
        default:
          return '#767676';
      }
    },
  },
};
</script>
